import { addMinutes, owaDate } from 'owa-datetime';
import { OCPSDataStore } from '../store/Store';
import { OCPSDataStorePolicyValue, OCPSDataStoreType } from '../store/schema/OCPSDataStoreType';

/**
 * Sets the value of a policy in store. If the key doesn't exist, creates one.
 * @param policyName
 * @param policyValue
 */
export const setPolicy = (policyName: string, policyValue: OCPSDataStorePolicyValue) => {
    OCPSDataStore.policies[policyName] = policyValue;
};

/**
 * Returns the value of a policy from store
 * @param policyName
 */
export const getPolicyValue = (policyName: string): OCPSDataStorePolicyValue =>
    OCPSDataStore.policies[policyName];

/**
 * Returns a boolean value indicating whether policyName is present in the store
 * @param policyName
 */
export const doesPolicyExist = (policyName: string) => policyName in OCPSDataStore.policies;

/**
 * Sets the expiration date in store using the checkInInterval param which defines after how
 * much time should new policies be fetched
 * @param checkInInterval
 */
export const setPolicyExpirationDate = (checkInInterval: string) => {
    const today = owaDate('UTC', new Date().toISOString());
    const newExpirationDate = addMinutes(today, parseInt(checkInInterval)).toString();
    OCPSDataStore.policyExpirationDate = newExpirationDate;
};

/**
 * Returns a boolean value indicating whether we have crossed previous policy expiration time
 */
export const havePreviousPoliciesExpired = () => {
    const { policyExpirationDate } = OCPSDataStore;

    if (!policyExpirationDate) {
        return true;
    }

    const hasExpired =
        owaDate('UTC') > owaDate('UTC', new Date(policyExpirationDate).toISOString());
    return hasExpired;
};

/**
 * Sets the hasError value in store
 * @param hasError
 */
export const setHasError = (hasError: boolean) => {
    OCPSDataStore.hasError = hasError;
};

/**
 * Returns a boolean indicating OCPSDataStore hasError value
 */
export const doesOCPSDataStoreHasError = () => !!OCPSDataStore.hasError;

/**
 * Sets the errorMessage value in store
 * @param errorMessage
 */
export const setOCPSErrorMessage = (errorMessage: string) => {
    OCPSDataStore.errorMessage = errorMessage;
};

/**
 * Returns errorMessage from store
 */
export const getOCPSErrorMessage = () => OCPSDataStore.errorMessage;

/**
 * Resets the store to empty values
 */
export const resetOCPSDataStore = () => {
    Object.assign(OCPSDataStore, {
        hasError: false,
        policies: {},
        policyExpirationDate: null,
        errorMessage: null,
    } as OCPSDataStoreType);
};
